/* eslint @backstage/no-undeclared-imports: 0 */
import { useState, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControlLabel,
  FormGroup,
  FormLabel,
  FormHelperText,
  FormControl,
  Checkbox,
  makeStyles,
  Theme,
  Box,
  Typography,
} from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) => ({
  fieldset: {
    marginTop: theme.spacing(1),
  },
  fieldsetContent: {
    padding: theme.spacing(1),
  },
  formLabel: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontWeight:
      typeof theme.typography.fontWeightMedium === 'number'
        ? theme.typography.fontWeightMedium
        : 500,
  },
  helperText: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

type PackageKey = 'core' | 'icons' | 'tokens';

type PackageState = {
  [key in PackageKey]: {
    value: string;
    label: string;
    selected: boolean;
  };
};

const defaultState: PackageState = {
  core: {
    label: 'eds-core-react',
    value: '@equinor/eds-core-react',
    selected: false,
  },
  icons: {
    label: 'eds-icons',
    value: '@equinor/eds-icons',
    selected: false,
  },
  tokens: {
    label: 'eds-tokens',
    value: '@equinor/eds-tokens',
    selected: false,
  },
};

const arraysEqual = (a: string[] = [], b: string[] = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

// TODO: Make this a reusable field extension that can be shared between different templates instead of hardcoded to EDS
export const EDSCheckboxGroup = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string[]>) => {
  const classes = useStyles();
  const getInitialState = (): PackageState => {
    if (Array.isArray(formData)) {
      return {
        core: { ...defaultState.core, selected: formData.includes(defaultState.core.value) },
        icons: { ...defaultState.icons, selected: formData.includes(defaultState.icons.value) },
        tokens: { ...defaultState.tokens, selected: formData.includes(defaultState.tokens.value) },
      };
    }
    return defaultState;
  };

  const [state, setState] = useState<PackageState>(getInitialState);

  useEffect(() => {
    if (Array.isArray(formData)) {
      const newState = getInitialState();
      if (
        state.core.selected !== newState.core.selected ||
        state.icons.selected !== newState.icons.selected ||
        state.tokens.selected !== newState.tokens.selected
      ) {
        setState(newState);
      }
    }
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const selectedPackages = Object.values(state)
      .filter(pkg => pkg.selected)
      .map(pkg => pkg.value);

    if (required && selectedPackages.length === 0) {
      if (formData !== undefined) {
        onChange(undefined);
      }
    } else if (!arraysEqual(formData, selectedPackages)) {
      onChange(selectedPackages);
    }
  }, [formData, onChange, required, state]);

  const { core, icons, tokens } = state;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name as PackageKey;
    const checked = event.target.checked;

    setState(prevState => {
      if (name === 'icons' && checked) {
        return {
          ...prevState,
          icons: {
            ...prevState.icons,
            selected: true,
          },
          core: {
            ...prevState.core,
            selected: true,
          },
        };
      }

      if (name === 'core' && !checked) {
        return {
          ...prevState,
          core: {
            ...prevState.core,
            selected: false,
          },
          icons: {
            ...prevState.icons,
            selected: false,
          },
        };
      }

      return {
        ...prevState,
        [name]: {
          ...prevState[name],
          selected: checked,
        },
      };
    });
  };

  const showError = required && rawErrors?.length > 0 && (!formData || formData.length === 0);

  return (
    <FormControl margin="none" required={required} error={rawErrors?.length > 0 && !formData}>
      <fieldset className={classes.fieldset}>
        <FormLabel component="legend" className={classes.formLabel}>
          Select which packages to include
        </FormLabel>
        <FormGroup className={classes.fieldsetContent}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={core.selected}
                onChange={handleChange}
                name="core"
              />
            }
            label={`${core.label} *`}
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={icons.selected}
                onChange={handleChange}
                name="icons"
              />
            }
            label={`${icons.label} **`}
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={tokens.selected}
                onChange={handleChange}
                name="tokens"
              />
            }
            label={tokens.label}
          />
          <Box className={classes.helperText}>
            <Typography variant="caption">
              * Will include the required styled-components package
            </Typography>
            <Typography variant="caption">
              ** eds-core-react is required to use eds-icons
            </Typography>

            {showError && (
              <FormHelperText error>Please select at least one package.</FormHelperText>
            )}
          </Box>
        </FormGroup>
      </fieldset>
    </FormControl>
  );
};
